import { useCallback, useMemo, useRef, useState } from 'react';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import { getContainer } from '@framework/di/getContainer';
import HeaderRepository from '@/creditflowresearch/infrastructure/repository/HeaderRepository';
import DictionaryMappingService from '@/creditflowresearch/domain/DictionaryMapping';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';

type TSearchResult = Partial<ReturnType<typeof DictionaryMappingService.mapSearchResult>>;

const useHeader = () => {
  const container = getContainer();
  const [searchText, setSearchText] = useState<string>('');
  const [searchResult, setSearchResult] = useState<TSearchResult>({});
  const [isSearchFetching, setIsSearchFetching] = useState<boolean>(false);
  const searchTextRef = useRef<string>(searchText);

  const updateSearchText = useCallback((text: string): void => {
    searchTextRef.current = text;
    setSearchText(text);
  }, []);

  const searchTextChanged = useCallback((text: string): void => {
    updateSearchText(text);
  }, [updateSearchText]);

  const resetSearchResult = useCallback((): void => {
    setSearchResult({});
    updateSearchText('');
  }, [updateSearchText]);

  const search = useCallback(async (productsList: IProduct[], regionsList: IRegion[]): Promise<void> => {
    const text = searchTextRef.current;

    if (!text?.trim()) {
      return;
    }

    setIsSearchFetching(true);
    try {
      const response = await container.get(HeaderRepository).search(text);
      setSearchResult(DictionaryMappingService.mapSearchResult(response, productsList, regionsList));
      setIsSearchFetching(false);
    } catch (error) {
      if (!Dispatcher.isAbortError(error)) {
        setIsSearchFetching(false);
      }
    }
  }, [container]);

  return useMemo(() => ({
    searchText,
    searchResult,
    isSearchFetching,
    searchTextChanged,
    search,
    resetSearchResult,
  }), [searchText, searchResult, isSearchFetching, searchTextChanged, search, resetSearchResult]);
};

export default useHeader;
